import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BlueprintBackground } from "@/components/BlueprintBackground";
import { api } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";

export default function PaymentSuccess() {
  const [status, setStatus] = useState("checking");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await refreshUser();
          if (!cancelled) setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(res.data.status)) {
          if (!cancelled) setStatus("error");
          return;
        }
      } catch (e) {
        // keep polling
      }
      attempts += 1;
      if (attempts >= 15) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      if (!cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="payment-success-page">
      <BlueprintBackground />
      <div className="w-full max-w-sm rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-md p-8 shadow-2xl text-center">
        {status === "checking" && (
          <>
            <Loader2 className="animate-spin text-blue-400 mx-auto mb-4" size={32} />
            <p className="text-slate-300" data-testid="payment-status-checking">
              Confirming your payment...
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="text-emerald-400 mx-auto mb-4" size={32} />
            <h1 className="font-heading text-xl font-bold text-white mb-2">You're on ModernTect Pro</h1>
            <p className="text-slate-400 text-sm mb-6">Unlimited projects and views are now unlocked.</p>
            <Button data-testid="payment-success-dashboard-button" onClick={() => navigate("/dashboard")} className="bg-blue-600 hover:bg-blue-700 w-full">
              Go to Dashboard
            </Button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <p className="text-rose-400 mb-4" data-testid="payment-status-error">
              We couldn't confirm your payment yet. If you completed checkout, it may take a moment - check your dashboard shortly.
            </p>
            <Link to="/dashboard" className="text-blue-400 hover:text-blue-300 text-sm font-medium">
              Back to Dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
